import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { search } from '@/api/search';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { SearchInput } from '@/components/search/SearchInput';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronRight, ShieldCheck } from 'lucide-react';
import { formatRef } from '@/lib/formatRef';

const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;

/**
 * Dedicated phone-or-name search bar mounted at the top of /auth-lists.
 * Calls the same /api/search endpoint as the global dashboard panel but
 * pre-filtered to `auth_list_entries`, so the result list is focused on
 * "which auth list(s) is this number on?".
 */
export function AuthListPhoneSearch() {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const customerCtxId = useEffectiveCustomerId();
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const enabled = debounced.length >= MIN_CHARS;
  const { data, isLoading } = useQuery({
    queryKey: ['auth-list-entries-search', debounced, customerCtxId],
    queryFn: () => search.run({
      q: debounced,
      types: ['auth_list_entries'],
      per_group_limit: 50,
      // Forward the active customer context so super_admin / reseller_owner
      // who have "entered" a customer see only that customer's lists.
      customer_id: customerCtxId ?? undefined,
    }),
    enabled,
    staleTime: 30_000,
  });

  const rows = data?.groups.auth_list_entries?.rows ?? [];

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-base">
          Search a phone or name across all auth lists
        </CardTitle>
      </CardHeader>
      <CardContent>
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Search phone number or name…"
        />
        {enabled && (
          <div className="mt-3">
            {isLoading ? (
              <div className="text-sm text-slate-400">Searching…</div>
            ) : rows.length === 0 ? (
              <div className="text-sm text-slate-500">
                Not found in any of your auth lists.
              </div>
            ) : (
              <div className="space-y-1">
                <div className="text-xs font-medium text-slate-500">
                  &ldquo;{debounced}&rdquo; found in {rows.length} {rows.length === 1 ? 'list' : 'lists'}:
                </div>
                <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
                  {rows.map((r) => {
                    const extra = r.extra as {
                      list_id: string;
                      list_name: string;
                      list_ref: string;
                      added_at: string;
                      label: string | null;
                      e164: string | null;
                    };
                    return (
                      <li
                        key={r.id}
                        className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2">
                            <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                            <span className="font-medium">{extra.list_name}</span>
                            <span className="text-[10px] font-mono text-slate-400">
                              {formatRef(extra.list_ref, 'AL')}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500">
                            {extra.e164 && <span className="font-mono">{extra.e164}</span>}
                            {extra.label && <span> · &ldquo;{extra.label}&rdquo;</span>}
                            <span> · added {new Date(extra.added_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/auth-lists/AL-${extra.list_ref}`)}
                        >
                          Open list <ChevronRight className="h-3 w-3 ms-1" />
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
